/**
 * Pure helpers for the two soil operator screens (U5). No React, no network,
 * no i18n — the same shape as soilGrouping.ts / soilSessionDetail.ts.
 */

export type SyncBannerState =
  | { kind: 'none' }
  | { kind: 'offlineIdle' }
  | { kind: 'offlineQueued'; count: number }
  | { kind: 'onlineQueued'; count: number };

/**
 * "Offline" and "work is queued" are INDEPENDENT conditions, and the banner
 * must not conflate them: a drain can fail while online (leaving a backlog on
 * a connected device), and being offline with an empty queue is information,
 * not an alarm. Keying a single message on navigator.onLine alone would be
 * wrong in the common case.
 */
export function resolveSyncBanner(online: boolean, pendingCount: number): SyncBannerState {
  if (!online) {
    return pendingCount > 0 ? { kind: 'offlineQueued', count: pendingCount } : { kind: 'offlineIdle' };
  }
  return pendingCount > 0 ? { kind: 'onlineQueued', count: pendingCount } : { kind: 'none' };
}

/**
 * Whole minutes since the group started, or null when it has not started or
 * the timestamp is unusable. Minute granularity is deliberate: the group
 * screen re-renders on this value, and a per-second clock is a re-render per
 * second on a battery-constrained field device.
 *
 * A future startedAt clamps to 0 — clock skew between a device and the server
 * must never render as negative elapsed time.
 */
export function elapsedMinutes(startedAt: string | null | undefined, now: Date): number | null {
  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return null;
  const diffMs = now.getTime() - started;
  if (diffMs <= 0) return 0;
  return Math.floor(diffMs / 60000);
}
